import { useEffect, useState } from "react";
import { Route, Lightbulb } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtMoney } from "@/lib/pricing";
import api from "@/lib/api";

// After a route is calculated, surfaces pending saved quotes whose stops are
// near the current route — check one to add its drop to the current route.
export default function NearbyQuotes({ routeData, stops, onToggleAddon }) {
  const [nearby, setNearby] = useState([]);

  useEffect(() => {
    const points = (routeData?.points || []).map((p) => [p.lat, p.lon]);
    if (points.length < 2) {
      setNearby([]);
      return;
    }
    let cancelled = false;
    api
      .post("/quotes/nearby", { points })
      .then(({ data }) => {
        if (!cancelled) setNearby(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [routeData]);

  if (!nearby.length) return null;

  return (
    <div
      className="mt-3 rounded-lg border border-[#F97316]/40 bg-[#FFF7ED] p-4"
      data-testid="nearby-quotes-panel"
    >
      <div className="flex items-center gap-2 mb-2">
        <Lightbulb className="h-4 w-4 text-[#EA580C]" />
        <h4 className="text-xs font-bold uppercase tracking-[0.1em] text-[#EA580C]">
          Possible add-on deliveries nearby
        </h4>
      </div>
      <p className="text-xs text-[#57534E] mb-2">
        These pending saved quotes have stops within 150 miles of this route — tick one to add its drop to this route and cut shipping cost.
      </p>
      <div className="space-y-1.5">
        {nearby.map((q) => {
          const added = (stops || []).some((s) => s.value === q.near_stop);
          return (
            <label
              key={q.id}
              className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[#1C1917] cursor-pointer"
              data-testid={`nearby-quote-${q.id}`}
            >
              <Checkbox
                checked={added}
                onCheckedChange={(v) => onToggleAddon(q, !!v)}
                className="h-3.5 w-3.5"
                data-testid={`nearby-quote-check-${q.id}`}
              />
              <Route className="h-3.5 w-3.5 text-[#F97316] shrink-0" />
              <span className="font-semibold">{q.client_name?.trim() || q.route_summary}</span>
              <span className="text-[#57534E]">
                · {q.nearest_miles} mi from this route ({q.near_stop}) · {q.status} · {fmtMoney(q.final_price)}
              </span>
              {added && <span className="text-[10px] font-bold uppercase text-[#EA580C]">added — recalculate</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
}
